import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { StringKey } from '../../../lang/index';
import { Column, CommonStats, ShareClassStats } from '../../../types/snapshot.types';
import { toRound } from '../../../utils/get-rounded-number.util';
import { toNumber } from '../../../utils/to-number.util';

export type PublicDetailsRow = { investment: number } & CommonStats;

type PublicDetailsMetricsProps = {
  row: PublicDetailsRow;
  visibleColumns: Column[];
};

type PublicDetailsItemProps = {
  avatar?: boolean;
  children?: ReactNode;
  className?: string;
  description: string;
  name: string;
  row: PublicDetailsRow;
  shareClasses: ShareClassStats[];
  visibleColumns: Column[];
};

const METRICS: [Column, StringKey, (row: PublicDetailsRow) => number | string][] = [
  [
    Column.ISSUED,
    'ISSUED_PERCENTAGE',
    ({ issuedPercentage }) => toRound(toNumber(issuedPercentage) * 100),
  ],
  [
    Column.DILUTED,
    'DILUTED_PERCENTAGE',
    ({ dilutedPercentage }) => toRound(toNumber(dilutedPercentage) * 100),
  ],
  [
    Column.VOTING,
    'VOTING_PERCENTAGE',
    ({ votingPercentage }) => toRound(toNumber(votingPercentage) * 100),
  ],
  [Column.NO_ISSUED, 'NO_ISSUED', ({ issued }) => issued.toLocaleString('en-US')],
  [Column.DILUTED_SHARES, 'DILUTED_SHARES', ({ diluted }) => diluted.toLocaleString('en-US')],
  // Grouped like the desktop table renders it — `toRound` alone printed 1000 without a separator.
  [
    Column.INVESTMENT,
    'INVESTMENT',
    ({ investment }) => Number(toRound(investment)).toLocaleString('en-US'),
  ],
];

/**
 * Only the columns the link shares are rendered. The figures behind the rest arrive zeroed, so a
 * fixed six-cell grid would show a withheld metric as `0.00` — a wrong number rather than an absent
 * one. The currency is left off because a public viewer has no selected company to read it from.
 */
export const PublicDetailsMetrics: FC<PublicDetailsMetricsProps> = ({ row, visibleColumns }) => {
  const cells = METRICS.filter(([column]) => visibleColumns.includes(column));

  if (cells.length === 0) return null;

  return (
    // Dividers come from the gap over a grey backing rather than from per-cell borders: the number
    // of cells varies with the link's selection, so which cell sits on an edge does too.
    <div className="grid grid-cols-2 gap-px border-y border-gray-100 bg-gray-100 text-sm font-[450] text-gray-700">
      {cells.map(([column, title, read], index) => (
        <div
          className={twMerge(
            'centered flex w-full flex-col gap-3 bg-white py-3',
            // A lone cell on the last row spans the width instead of leaving a grey half.
            index === cells.length - 1 && cells.length % 2 === 1 && 'col-span-2',
          )}
          key={column}
        >
          <span className="text-label-md font-medium">
            <AppFormattedMessage id={title} />
          </span>
          <span>{read(row)}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * One cap table row as a card, which is how the mobile design presents what the desktop table puts
 * in columns.
 */
export const PublicDetailsItem: FC<PublicDetailsItemProps> = ({
  avatar,
  children,
  className,
  description,
  name,
  row,
  shareClasses,
  visibleColumns,
}) => (
  <div className={twMerge('flex w-full flex-col gap-2 rounded-md py-3 shadow-sm', className)}>
    <div className="flex w-full items-center gap-4 border-b-2 border-b-gray-100 px-3 pb-3">
      {avatar && (
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-[450] text-gray-700">
          {name[0]}
        </div>
      )}
      <div className="flex flex-col overflow-hidden">
        <span className="max-w-full truncate text-base font-semibold text-gray-700">{name}</span>
        <span className="text-xs font-[450] text-gray-400">{description}</span>
      </div>
    </div>

    <PublicDetailsMetrics row={row} visibleColumns={visibleColumns} />

    {shareClasses.length > 0 && (
      <div className="flex flex-col gap-2 px-2">
        {shareClasses.map(({ id, issued, name: shareClassName }) => (
          <div
            className="centered flex w-full flex-col gap-1 rounded-lg bg-gray-50 px-4 py-2 text-gray-700"
            key={id}
          >
            <span className="max-w-full truncate text-label-md font-medium">{shareClassName}</span>
            <span className="text-sm font-[450]">{issued.toLocaleString('en-US')}</span>
          </div>
        ))}
      </div>
    )}

    {children}
  </div>
);
